import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Problem, ProblemListRepositoryService, ReportControl } from '@xmedical/core';
import { ReportControlEditorService } from '../../services/report-control-editor.service';

/**
 * ProblemListRendererComponent
 *
 * Renders a list of patient problems in a report.
 * Uses a refreshTrigger input signal instead of PatientEventsService.
 * The consuming app should increment refreshTrigger when problems change.
 */
@Component({
  selector: 'xmed-problem-list-renderer',
  standalone: true,
  imports: [
    MatListModule,
    MatFormFieldModule
  ],
  templateUrl: './problem-list-renderer.component.html',
  styleUrl: './problem-list-renderer.component.scss'
})
export class ProblemListRendererComponent implements OnInit {
  private problemListRepository = inject(ProblemListRepositoryService);
  private editorService = inject(ReportControlEditorService);

  patientId = input.required<number>();
  control = input.required<ReportControl>();
  refreshTrigger = input<number>(0);

  problemList = signal<Problem[]>([]);

  // Effect to reload when refreshTrigger changes
  private refreshEffect = effect(() => {
    const trigger = this.refreshTrigger();
    const pid = this.patientId();
    if (trigger > 0 && pid) {
      this.loadProblems();
    }
  });

  ngOnInit(): void {
    this.problemList.set(this.control().problemListValue || []);
    this.loadProblems();
  }

  private loadProblems(): void {
    this.problemListRepository.loadPatientProblemList(this.patientId()).subscribe(problems => {
      const sorted = problems.sort((a, b) => a.createdDate > b.createdDate ? 1 : -1);
      this.problemList.set(sorted);
      this.control().problemListValue = sorted;
      this.editorService.triggerEvent();
    });
  }
}
